/** ADM-22 Paiements · ADM-23 Rapprochement des virements · ADM-24 Encaissements CB au retrait · ADM-25 Remboursements (◆). */
import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Modal, Pagination, SearchField, SelectField, Table, TextField, TextareaField, useToast } from '@myron/design-system';
import { CheckCircle2, Undo2 } from 'lucide-react';
import type { Page, S } from '@myron/contracts';
import { get, nouvelleCleIdempotence, post } from '../../../shared/api/client';
import { SiPermission, useSession } from '../../../shared/auth/session';
import { DecisionOuverte, EntetePage, EtatRequete, ErreurFormulaire, MentionSimule, StatutBadge } from '../../../shared/ui/composants';
import { dateCourte, dateHeure, euros, LIBELLE_MOYEN } from '../../../shared/ui/format';
import { BarreFiltres, nomEntrepot, useEntrepots, versCentimes, aujourdhuiIso } from '../commercial/commun';
import { ModaleEncaissementCb } from './Commandes';

type Paiement = S<'Paiement'> & { raisonSociale?: string; commandeStatut?: string; entrepotId?: string; dejaRembourseCentimes?: number; creeLe?: string };
type Remboursement = S<'Remboursement'> & { commandeReference?: string; moyen?: string; creeLe?: string };

export function PagePaiements() {
  const [statut, setStatut] = useState('');
  const [moyen, setMoyen] = useState('');
  const [recherche, setRecherche] = useState('');
  const [page, setPage] = useState(1);
  const [rembourser, setRembourser] = useState<Paiement | null>(null);
  const { peut } = useSession();
  const q = useQuery({ queryKey: ['admin', 'paiements', statut, moyen, recherche, page], queryFn: () => get<Page<Paiement>>('/admin/paiements', { statut, moyen, q: recherche, page, taille: 25 }) });
  return (
    <>
      <EntetePage titre="Paiements" sousTitre={<>Aucune donnée de carte n’est stockée par MYRON. <MentionSimule>Paiements SIMULÉS</MentionSimule></>} actions={
        <>
          <Link to="/admin/paiements/virements" className="my-btn my-btn--secondaire">Rapprocher les virements</Link>
          <Link to="/admin/paiements/cb-retrait" className="my-btn my-btn--secondaire">CB au retrait</Link>
        </>
      } />
      <BarreFiltres>
        <SearchField label="Rechercher un paiement" placeholder="Commande, client, référence de virement" value={recherche} onChange={(e) => { setRecherche(e.target.value); setPage(1); }} />
        <SelectField label="Statut" value={statut} onChange={(e) => { setStatut(e.target.value); setPage(1); }} options={[{ value: '', label: 'Tous' }, ...['EN_ATTENTE', 'CONFIRME', 'REMBOURSE_PARTIEL', 'REMBOURSE', 'ECHOUE', 'ANNULE', 'EXPIRE'].map((s) => ({ value: s, label: s.replace('_', ' ').toLowerCase() }))]} />
        <SelectField label="Moyen" value={moyen} onChange={(e) => { setMoyen(e.target.value); setPage(1); }} options={[{ value: '', label: 'Tous' }, ...Object.entries(LIBELLE_MOYEN).map(([value, label]) => ({ value, label }))]} />
      </BarreFiltres>
      <EtatRequete requete={q} vide={(d) => d.total === 0} titreVide="Aucun paiement" enfants={(d) => (
        <>
          <Table<Paiement> legende="Paiements" lignes={d.donnees} cle={(p) => p.id!} colonnes={[
            { cle: 'c', titre: 'Commande', rendu: (p) => <Link to={`/admin/commandes/${p.commandeId}`}>{p.commandeReference}</Link> },
            { cle: 'cl', titre: 'Client', rendu: (p) => p.raisonSociale ?? '—' },
            { cle: 'm', titre: 'Moyen', rendu: (p) => LIBELLE_MOYEN[p.moyen!] },
            { cle: 'mt', titre: 'Montant', num: true, rendu: (p) => euros(p.montantCentimes) },
            { cle: 'd', titre: 'Confirmé', rendu: (p) => (p.confirmeLe ? `${dateCourte(p.confirmeLe)} · ${p.confirmePar ?? ''}` : '—') },
            { cle: 's', titre: 'Statut', rendu: (p) => <StatutBadge statut={p.statut!} /> },
            ...(peut('paiement.rembourser') ? [{ cle: 'a', titre: <span className="my-sr">Actions</span>, actions: true, rendu: (p: Paiement) => (p.statut === 'CONFIRME' || p.statut === 'REMBOURSE_PARTIEL') ? <Button compact variante="tertiaire" icone={Undo2} onClick={() => setRembourser(p)} aria-label={`Rembourser ${p.commandeReference}`}>Rembourser</Button> : null }] : []),
          ]} />
          <Pagination page={d.page} total={d.total} taille={d.taille} onPage={setPage} />
        </>
      )} />
      {rembourser && <ModaleRemboursement paiement={rembourser} onFermer={() => setRembourser(null)} />}
    </>
  );
}

export function PageVirements() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'paiements', 'virements'], queryFn: () => get<Page<Paiement>>('/admin/paiements', { statut: 'EN_ATTENTE', moyen: 'VIREMENT', taille: 100 }) });
  const [choisi, setChoisi] = useState<Paiement | null>(null);
  return (
    <>
      <EntetePage titre="Rapprochement des virements" fil={[{ libelle: 'Paiements', vers: '/admin/paiements' }, { libelle: 'Virements' }]} sousTitre="Confirmez un virement reçu après vérification du relevé bancaire : la commande est alors payée et son stock réservé." />
      <DecisionOuverte id="DO-06">Rapprochement manuel en V1 (import de relevés ou API bancaire à arbitrer). Les relevés simulés arrivent dans la boîte e-mail du panneau de simulation.</DecisionOuverte>
      <div style={{ marginTop: 'var(--space-4)' }}>
        <EtatRequete requete={q} vide={(d) => d.total === 0} titreVide="Aucun virement attendu" enfants={(d) => (
          <Table<Paiement> legende="Virements attendus" lignes={d.donnees} cle={(p) => p.id!} colonnes={[
            { cle: 'c', titre: 'Commande', rendu: (p) => <Link to={`/admin/commandes/${p.commandeId}`}>{p.commandeReference}</Link> },
            { cle: 'cl', titre: 'Client', rendu: (p) => p.raisonSociale ?? '—' },
            { cle: 'r', titre: 'Référence attendue', rendu: (p) => <code className="my-code">{p.referenceVirementAttendue ?? p.commandeReference}</code> },
            { cle: 'm', titre: 'Montant', num: true, rendu: (p) => euros(p.montantCentimes) },
            { cle: 'd', titre: 'Commande du', rendu: (p) => dateCourte(p.creeLe) },
            { cle: 'a', titre: <span className="my-sr">Actions</span>, actions: true, rendu: (p) => <SiPermission permission="paiement.confirmer"><Button compact icone={CheckCircle2} onClick={() => setChoisi(p)} aria-label={`Confirmer le virement ${p.commandeReference}`}>Confirmer</Button></SiPermission> },
          ]} />
        )} />
      </div>
      {choisi && <ModaleVirement paiement={choisi} onFermer={() => setChoisi(null)} onOk={() => { setChoisi(null); void qc.invalidateQueries({ queryKey: ['admin'] }); }} />}
    </>
  );
}

function ModaleVirement({ paiement, onFermer, onOk }: { paiement: Paiement; onFermer: () => void; onOk: () => void }) {
  const toast = useToast();
  const cle = useRef(nouvelleCleIdempotence());
  const [montant, setMontant] = useState(((paiement.montantCentimes ?? 0) / 100).toFixed(2).replace('.', ','));
  const [date, setDate] = useState(aujourdhuiIso());
  const [libelle, setLibelle] = useState(paiement.referenceVirementAttendue ?? paiement.commandeReference ?? '');
  const m = useMutation({
    mutationFn: () => post<S<'ResultatConfirmationPaiement'>>(`/admin/paiements/${paiement.id}/confirmer-virement`, { montantRecuCentimes: versCentimes(montant), dateValeur: date, libelleVirement: libelle }, { idempotence: cle.current, version: paiement.version }),
    onSuccess: (r) => { toast({ ton: r.reservation === 'RESERVEE' ? 'succes' : 'alerte', titre: 'Virement confirmé', texte: r.reservation === 'RESERVEE' ? 'Commande payée, stock réservé.' : 'Commande payée mais stock insuffisant (DO-04).' }); onOk(); },
  });
  return (
    <Modal ouverte titre={`Confirmer le virement ${paiement.commandeReference}`} onFermer={onFermer} actions={<><Button variante="tertiaire" onClick={onFermer}>Annuler</Button><Button chargement={m.isPending} onClick={() => m.mutate()}>Confirmer la réception</Button></>}>
      <div className="my-form">
        <p>Montant attendu : <strong className="my-prix">{euros(paiement.montantCentimes)}</strong></p>
        <TextField label="Montant reçu (€)" inputMode="decimal" value={montant} onChange={(e) => setMontant(e.target.value)} required />
        <TextField label="Date de valeur" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        <TextField label="Libellé du virement" value={libelle} onChange={(e) => setLibelle(e.target.value)} required />
        <ErreurFormulaire erreur={m.error} />
      </div>
    </Modal>
  );
}

/** Une commande PRETE n'est à encaisser qu'en variante R-B (préparée sans réservation, donc non payée). */
const aEncaisser = <T extends { statut?: string; allocations?: unknown[] }>(l: T[]) => l.filter((c) => c.statut !== 'PRETE' || !(c.allocations ?? []).length);

/** Commandes CB au retrait en attente d'encaissement (dans la portée de l'utilisateur). */
export function PageEncaissementsCb() {
  const qc = useQueryClient();
  const entrepots = useEntrepots();
  const q = useQuery({ queryKey: ['admin', 'commandes', 'cb-retrait'], queryFn: () => get<Page<S<'CommandeAdmin'> & { entreprise?: { raisonSociale?: string } }>>('/admin/commandes', { moyenPaiement: 'CB_RETRAIT', statut: 'EN_ATTENTE_PAIEMENT,EN_PREPARATION_NON_RESERVEE,PRETE', taille: 100 }) });
  const variantes = useQuery({ queryKey: ['simulation', 'etat'], queryFn: () => get<{ variantes: { varianteRetrait?: string } }>('/_simulation/etat'), retry: false });
  const [choisie, setChoisie] = useState<S<'CommandeAdmin'> | null>(null);
  return (
    <>
      <EntetePage titre="Encaissements CB au retrait" fil={[{ libelle: 'Paiements', vers: '/admin/paiements' }, { libelle: 'CB au retrait' }]} />
      <DecisionOuverte id="DO-01">
        Variante simulée : <strong>{variantes.data?.variantes.varianteRetrait ?? 'R-A'}</strong>. R-A : réservation et préparation après encaissement. R-B : préparation sans réservation, réservation à l’encaissement. R-C : acompte en ligne.
      </DecisionOuverte>
      <div style={{ marginTop: 'var(--space-4)' }}>
        <EtatRequete requete={q} vide={(d) => !aEncaisser(d.donnees).length} titreVide="Aucun encaissement en attente" enfants={(d) => (
          <Table legende="Commandes à encaisser" lignes={aEncaisser(d.donnees)} cle={(c) => c.id!} colonnes={[
            { cle: 'r', titre: 'Commande', rendu: (c) => <Link to={`/admin/commandes/${c.id}`}>{c.reference}</Link> },
            { cle: 'cl', titre: 'Client', rendu: (c) => c.entreprise?.raisonSociale ?? '—' },
            { cle: 'e', titre: 'Entrepôt', rendu: (c) => nomEntrepot(entrepots.data, c.entrepotId) },
            { cle: 'cr', titre: 'Créneau', rendu: (c) => (c.creneauRetrait ? `${c.creneauRetrait.date} ${c.creneauRetrait.debut}` : '—') },
            { cle: 'm', titre: 'Montant', num: true, rendu: (c) => euros(c.totalTtcCentimes) },
            { cle: 's', titre: 'Statut', rendu: (c) => <StatutBadge statut={c.statut!} /> },
            { cle: 'a', titre: <span className="my-sr">Actions</span>, actions: true, rendu: (c) => <Button compact onClick={() => setChoisie(c)} aria-label={`Encaisser ${c.reference}`}>Encaisser</Button> },
          ]} />
        )} />
      </div>
      {choisie && <ModaleEncaissementCb commande={choisie} onFermer={() => setChoisie(null)} onOk={() => { setChoisie(null); void qc.invalidateQueries({ queryKey: ['admin'] }); }} />}
    </>
  );
}

function ModaleRemboursement({ paiement, onFermer }: { paiement: Paiement; onFermer: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const cle = useRef(nouvelleCleIdempotence());
  const restant = (paiement.montantCentimes ?? 0) - (paiement.dejaRembourseCentimes ?? 0);
  const [montant, setMontant] = useState((restant / 100).toFixed(2).replace('.', ','));
  const [motif, setMotif] = useState('');
  const m = useMutation({
    mutationFn: () => post<Remboursement | S<'DemandeValidation'>>('/admin/remboursements', { paiementId: paiement.id, montantCentimes: versCentimes(montant), motif: motif.trim() }, { idempotence: cle.current }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['admin'] });
      const attente = r.statut === 'EN_ATTENTE_VALIDATION';
      toast({ ton: attente ? 'info' : 'succes', titre: attente ? 'Double validation requise' : 'Remboursement effectué (simulé)', texte: attente ? 'Un second utilisateur habilité doit valider la demande.' : undefined });
      onFermer();
    },
  });
  return (
    <Modal ouverte titre={`Rembourser ${paiement.commandeReference}`} onFermer={onFermer} actions={<><Button variante="tertiaire" onClick={onFermer}>Annuler</Button><Button chargement={m.isPending} disabled={motif.trim().length < 3} onClick={() => m.mutate()}>Demander le remboursement</Button></>}>
      <div className="my-form">
        <p>Restant remboursable : <strong>{euros(restant)}</strong> ({LIBELLE_MOYEN[paiement.moyen!]})</p>
        <TextField label="Montant (€)" inputMode="decimal" value={montant} onChange={(e) => setMontant(e.target.value)} required />
        <TextareaField label="Motif" value={motif} onChange={(e) => setMotif(e.target.value)} required />
        <Alert ton="info">Au-delà du seuil paramétré, le remboursement exige la validation d’un second utilisateur (quatre yeux).</Alert>
        <ErreurFormulaire erreur={m.error} />
      </div>
    </Modal>
  );
}

export function PageRemboursements() {
  const [page, setPage] = useState(1);
  const q = useQuery({ queryKey: ['admin', 'remboursements', page], queryFn: () => get<Page<Remboursement>>('/admin/remboursements', { page, taille: 25 }) });
  return (
    <>
      <EntetePage titre="Remboursements" fil={[{ libelle: 'Paiements', vers: '/admin/paiements' }, { libelle: 'Remboursements' }]} sousTitre={<>Remboursements simulés : aucune transaction réelle. Les demandes au-delà du seuil apparaissent dans l’écran des validations. <MentionSimule /></>} />
      <EtatRequete requete={q} vide={(d) => d.total === 0} titreVide="Aucun remboursement" enfants={(d) => (
        <>
          <Table<Remboursement> legende="Remboursements" lignes={d.donnees} cle={(r) => r.id!} colonnes={[
            { cle: 'd', titre: 'Demandé le', rendu: (r) => dateHeure(r.creeLe) },
            { cle: 'c', titre: 'Commande', rendu: (r) => r.commandeReference ?? '—' },
            { cle: 'm', titre: 'Montant', num: true, rendu: (r) => euros(r.montantCentimes) },
            { cle: 'mo', titre: 'Motif', rendu: (r) => r.motif },
            { cle: 'p', titre: 'Demandé par', rendu: (r) => r.demandePar ?? '—' },
            { cle: 'v', titre: 'Validé par', rendu: (r) => r.validePar ?? '—' },
            { cle: 's', titre: 'Statut', rendu: (r) => <Badge ton={r.statut === 'EFFECTUE' ? 'succes' : r.statut === 'REJETE' ? 'erreur' : 'alerte'}>{({ EFFECTUE: 'Effectué', EN_COURS: 'En cours', EN_ATTENTE_VALIDATION: 'En attente de validation', REJETE: 'Rejeté' } as Record<string, string>)[r.statut!] ?? r.statut}</Badge> },
          ]} />
          <Pagination page={d.page} total={d.total} taille={d.taille} onPage={setPage} />
        </>
      )} />
      <Card style={{ marginTop: 16 }}><p className="my-petit">Pour approuver une demande en attente : <Link to="/admin/validations">Validations à quatre yeux</Link>.</p></Card>
    </>
  );
}
